import { useMemo } from "react";
import { Task, TaskInstance, InstanceStatus, RecurrencePattern } from "@/lib/api/checklist";
import { getPeriodsForRecurrence, groupInstancesByPeriod, getDateRangeForRecurrence } from "@/lib/utils/recurrence";
import { Check, Clock, AlertCircle, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";

interface YearlyBreakdownViewProps {
  tasks: Task[];
  instances: TaskInstance[];
  lookbackYears: number;
  isFiscalYear?: boolean;
}

export function YearlyBreakdownView({ tasks, instances, lookbackYears, isFiscalYear = false }: YearlyBreakdownViewProps) {
  // Calculate date range and periods based on fiscal vs calendar year
  const dateRange = useMemo(() => {
    if (isFiscalYear) {
      // For fiscal year, we need to calculate from July to June
      const now = moment();
      const currentFiscalYear = now.month() >= 6 ? now.year() + 1 : now.year();
      
      // Start from July of (currentFiscalYear - lookbackYears - 1)
      const startYear = currentFiscalYear - lookbackYears;
      const start = moment().year(startYear).month(6).startOf('month'); // July
      
      // End at June of current fiscal year
      const end = moment().year(currentFiscalYear).month(5).endOf('month'); // June
      
      return { start: start.toDate(), end: end.toDate() };
    } else {
      return getDateRangeForRecurrence(RecurrencePattern.ANNUALLY, lookbackYears);
    }
  }, [lookbackYears, isFiscalYear]);
  
  // Get periods - for fiscal year, we need custom periods
  const periods = useMemo(() => {
    if (isFiscalYear) {
      const periodList = [];
      const start = moment(dateRange.start);
      const end = moment(dateRange.end);
      
      let current = start.clone();
      while (current.isSameOrBefore(end)) {
        const fiscalYearStart = current.clone();
        const fiscalYearEnd = current.clone().add(11, 'months').endOf('month');
        
        if (fiscalYearEnd.isSameOrAfter(start) && fiscalYearStart.isSameOrBefore(end)) {
          const fiscalYear = fiscalYearEnd.year(); // Fiscal year is named after the ending year
          periodList.push({
            label: fiscalYear.toString(),
            start: fiscalYearStart.toDate(),
            end: fiscalYearEnd.toDate(),
            key: `fiscal-year-${fiscalYear}`,
          });
        }
        
        current.add(1, 'year');
      }
      
      return periodList;
    } else {
      return getPeriodsForRecurrence(RecurrencePattern.ANNUALLY, dateRange.start, dateRange.end);
    }
  }, [dateRange, isFiscalYear]);
  
  // Sort periods by year descending (newest first)
  const sortedPeriods = useMemo(() => {
    return [...periods].sort((a, b) => {
      const yearA = parseInt(a.label);
      const yearB = parseInt(b.label);
      return yearB - yearA;
    });
  }, [periods]);
  
  // Group instances by task and period
  const instancesByTaskAndPeriod = useMemo(() => {
    const grouped = new Map<string, Map<string, TaskInstance[]>>();
    
    tasks.forEach(task => {
      const taskInstances = instances.filter(inst => inst.task_id === task.id);
      const periodMap = groupInstancesByPeriod(taskInstances, periods);
      grouped.set(task.id, periodMap);
    });
    
    return grouped;
  }, [tasks, instances, periods]);
  
  // Get task deadline from first instance
  const getTaskDeadline = (task: Task): string => {
    const taskInstances = instances.filter(inst => inst.task_id === task.id);
    if (taskInstances.length > 0) {
      const firstInstance = taskInstances.sort((a, b) => 
        new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
      )[0];
      return moment.tz(firstInstance.due_date, CommonDestinationTimezone).format("MMMM Do");
    }
    return "";
  };
  
  // Get status for a task's year
  const getYearStatus = (task: Task, periodKey: string) => {
    const taskPeriodMap = instancesByTaskAndPeriod.get(task.id);
    const periodInstances = taskPeriodMap?.get(periodKey) || [];
    
    if (periodInstances.length === 0) {
      const period = periods.find(p => p.key === periodKey);
      if (period && period.start > new Date()) {
        return { status: 'future', label: '', icon: null };
      }
      return { status: 'no-data', label: '', icon: null };
    }
    
    const completed = periodInstances.find(inst => inst.status === InstanceStatus.COMPLETED);
    const pending = periodInstances.find(inst => inst.status === InstanceStatus.PENDING);
    
    if (completed) {
      return {
        status: completed.is_late ? 'completed-late' : 'completed',
        label: completed.is_late ? 'Done Late' : 'Done',
        icon: <Check className="h-3 w-3" />,
      };
    }
    
    if (pending) {
      return {
        status: pending.is_late ? 'late' : 'pending',
        label: pending.is_late ? 'Late' : 'Pending',
        icon: pending.is_late ? <AlertCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />,
      };
    }
    
    return { status: 'unknown', label: '', icon: null };
  };
  
  // Sort tasks: those with data first, then those without
  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      const aInstances = instances.filter(inst => inst.task_id === a.id);
      const bInstances = instances.filter(inst => inst.task_id === b.id);
      
      // Tasks with instances come first
      if (aInstances.length > 0 && bInstances.length === 0) return -1;
      if (aInstances.length === 0 && bInstances.length > 0) return 1;
      
      // If both have instances or both don't, maintain original order
      return 0;
    });
  }, [tasks, instances]);
  
  return (
    <div className="space-y-1">
      {/* Header with year labels */}
      <div className="flex gap-1 mb-2">
        <div className="w-[280px]" /> {/* Space for task names */}
        {sortedPeriods.map((period, index) => {
          const year = parseInt(period.label);
          const isInRange = true; // All periods returned are in range now
          
          return (
            <div key={period.key} className="flex-1 min-w-[80px]">
              <div className="text-center">
                <div 
                  className={cn(
                    "text-sm font-medium rounded px-2 py-1",
                    "bg-yellow-100 text-yellow-800"
                  )}
                >
                  <b>{isFiscalYear ? `FY${year}` : year}</b>
                  {isFiscalYear && (
                    <div className="text-[10px] opacity-80">
                      Jul - Jun
                    </div>
                  )}
                </div>
              </div>
              {isFiscalYear && (
                <div 
                  className={cn(
                    "h-1 mt-1 rounded-full",
                    // Alternate colors for fiscal years
                    (index % 2 === 0) 
                      ? "bg-purple-400" 
                      : "bg-teal-400"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
      
      {/* Tasks */}
      {sortedTasks.map(task => {
        const deadline = getTaskDeadline(task);
        
        return (
          <div key={task.id} className="flex gap-1 items-center group hover:bg-muted/50 rounded-lg p-1 -m-1">
            {/* Task Info */}
            <div className="w-[280px] pr-2">
              <div className="text-sm font-medium truncate">{task.title}</div>
              {deadline && (
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {deadline}
                </div>
              )}
            </div>
            
            {/* Years */}
            {sortedPeriods.map(period => {
              const { status, label, icon } = getYearStatus(task, period.key);
              const isFuture = period.start > new Date();
              const hasData = status !== 'no-data' && status !== 'future';
              
              return (
                <div
                  key={period.key}
                  className={cn(
                    "flex-1 min-w-[80px] rounded px-2 py-1 text-center transition-all",
                    "flex items-center justify-center gap-1",
                    "border-2",
                    // Border styles
                    hasData && !isFuture && "border-indigo-400",
                    !hasData && !isFuture && "border-transparent",
                    isFuture && "border-transparent opacity-50",
                    // Background styles
                    status === 'completed' && "bg-green-100 text-green-700",
                    status === 'completed-late' && "bg-orange-100 text-orange-700",
                    status === 'pending' && "bg-blue-100 text-blue-700",
                    status === 'late' && "bg-red-100 text-red-700",
                    status === 'no-data' && !isFuture && "border-gray-500",
                    isFuture && "bg-gray-100 text-gray-400"
                  )}
                >
                  {icon}
                  {label && <span className="text-xs font-medium">{label}</span>}
                  {(!label && status === 'no-data') && <span className="text-xs font-medium">No Data</span>}
                </div>
              );
            })}
          </div>
        );
      })}
      
      {/* Compact Legend */}
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-4 pt-2 border-t">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-100" />
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-orange-100" />
          <span>Completed Late</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-100" />
          <span>Pending</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-red-100" />
          <span>Late</span>
        </div>
      </div>
    </div>
  );
}
